import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import { PermissionEnum } from '@Application/Security/Roles';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { InterviewDetailsSource } from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail/resolveInterviewDetail';
import useRetrieveInterviewDetails from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail';
import FetchTranscript from '@Application/Beneficiaries/Transcripts/Queries/FetchTranscript';
import fetchTranscript from '@Application/Beneficiaries/Transcripts/fetchTranscript';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import PageHeader from '@Guidelines/Layout/PageHeader';
import InterviewTitle from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/InterviewTitle';
import DeleteInterviewAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/DeleteInterviewAction';
import InterviewParticipantBadges from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/Participants/InterviewParticipantBadges';
import TranscriptPendingNotice from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptPendingNotice';
import TranscriptReviewSection from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection';
import useSubjectModifiability from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Hooks/useSubjectModifiability';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import { css } from '@emotion/react';
import * as React from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Skeleton } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import interviewDeletionInProgressSignal, {
	endInterviewDeletion,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/Signals/interviewDeletionInProgressSignal';
import { useSignalValue } from '@verclam/pulsar';

const skeletonStackStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const rootStyle = css`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		overflow-y: auto;
	}
`;

const consentLinkStyle = css`
	display: inline-flex;
	align-items: center;
	padding: 0.5rem 1rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
	text-decoration: none;
	border: 1.5px solid ${theme.colors.borderStrong};
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.surface};

	&:hover,
	&:focus-visible {
		background-color: ${theme.colors.surfaceMuted};
	}
`;

const scrollAreaStyle = css`
	flex: 1;
	min-height: 0;
	overflow-y: auto;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		flex: none;
		overflow-y: visible;
	}
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly interviewDetailsSource: InterviewDetailsSource;
};

/**
 * Read-only return screen: owns the fetch and every loading/error/not-found/nominal branch. Branch
 * order follows `InterviewConsentDetailsContent` (error, loading, not-found, nominal), not the union's.
 *
 * It owns the transcript fetch because it alone decides whether the review block is mounted — that
 * fetch cannot be delegated to a child it may not mount. `useStoreQuery` is therefore declared ahead
 * of every early return, and a `query` that is a no-op `Promise.resolve()` while the recording is not
 * stored server-side yet.
 */
const InterviewDetailsContent: React.FC<Props> = ({ subject, interviewDetailsSource }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const { canModify: isWritable } = useSubjectModifiability(subject);

	const interviewDetailResult = useRetrieveInterviewDetails(subject, interviewDetailsSource);

	// The interview being deleted leaves the store before the navigation: the screen stays on its skeleton meanwhile.
	const deletionInProgressId = useSignalValue(interviewDeletionInProgressSignal);
	const isDeletionInProgress =
		interviewDetailsSource.type === 'server' &&
		deletionInProgressId === interviewDetailsSource.interviewId;

	const deletableInterviewId =
		interviewDetailsSource.type === 'server' ? interviewDetailsSource.interviewId : null;

	// A signal left on a deleted id would keep a later visit to this address on a skeleton forever.
	React.useEffect(() => {
		return () => {
			if (
				deletableInterviewId !== null &&
				interviewDeletionInProgressSignal.peek() === deletableInterviewId
			) {
				endInterviewDeletion();
			}
		};
	}, [deletableInterviewId]);

	// Readable before any early return below, which keeps every hook that follows unconditional.
	const interviewDetail =
		interviewDetailResult.type === 'resolved' ? interviewDetailResult.interviewDetails : null;
	const interviewId = interviewDetail !== null ? interviewDetail.interviewId : null;
	const hasTranscriptSource = interviewDetail?.hasTranscriptSource === true;
	const processingStateValue = interviewDetail?.processingState?.value ?? null;

	const { data: transcriptDetails } = useStoreQuery({
		selector: (state) => state.interviewsStore.transcript,
		stopOn: (value) => value !== null && !!interviewId && value.interviewId === interviewId,
		query: () => {
			if (interviewId === null || !hasTranscriptSource) {
				return Promise.resolve();
			}

			return dispatch(fetchTranscript(new FetchTranscript(facilityId, subject, interviewId)));
		},
		deps: [facilityId, subject, interviewId, hasTranscriptSource, processingStateValue],
	});

	const handleReloadApplication = React.useCallback(() => {
		window.location.reload();
	}, []);

	const handleGenerateDocuments = React.useCallback(() => {
		if (interviewId === null) {
			return;
		}

		navigate(`${buildSubjectPath(subject)}/documents/new?interview=${interviewId}`);
	}, [navigate, subject, interviewId]);

	// Shortcut for "Générer des documents" from the prototype's transcript screen: shown once the
	// interview's OWN transcript has resolved (`ownsTranscript`, the same guard the review section
	// below relies on) — the destination screen re-checks selectability against the server itself.
	const hasLoadedTranscript = interviewDetail?.ownsTranscript(transcriptDetails) ?? false;
	const canSeeConsent = interviewId !== null && (interviewDetail?.consent?.consentId ?? null) !== null;

	const transcriptReviewSectionProps = React.useMemo(() => {
		if (!interviewDetail) {
			return null;
		}

		const subjectTranscript = interviewDetail.ownsTranscript(transcriptDetails)
			? transcriptDetails
			: null;

		if (subjectTranscript === null) {
			return null;
		}

		return {
			facilityId,
			interviewId: subjectTranscript.interviewId,
			transcript: subjectTranscript,
			recordingStatus: interviewDetail.recordingStatus,
			recordingId:
				interviewDetail.recordingStatus === 'stored' && interviewDetail.recording !== null
					? interviewDetail.recording.id
					: null,
			participants: interviewDetail.participants,
		};
	}, [transcriptDetails, facilityId, interviewDetail]);

	if (isDeletionInProgress || interviewDetailResult.type === 'loading') {
		return (
			<div css={skeletonStackStyle}>
				<Skeleton height="3rem" animation="wave" shape="rectangle" />
				<Skeleton height="8rem" animation="wave" shape="rectangle" />
				<Skeleton height="8rem" animation="wave" shape="rectangle" />
				<Skeleton height="8rem" animation="wave" shape="rectangle" />
			</div>
		);
	}

	if (interviewDetailResult.type === 'error') {
		return (
			<React.Fragment>
				<PageHeader eyebrow="ENTRETIENS" title="Enregistrement" />
				<ErrorState
					title="Impossible de charger l'enregistrement"
					onRetry={interviewDetailResult.refetch}
				/>
			</React.Fragment>
		);
	}

	// The hook lets a resolved subject win over a server failure; each screen sets its own precedence.
	// Here the failure wins: a consultation screen never renders a half-loaded subject in silence.
	if (interviewDetailResult.type === 'resolved' && interviewDetailResult.error !== null) {
		return (
			<React.Fragment>
				<PageHeader eyebrow="ENTRETIENS" title="Enregistrement" />
				<ErrorState
					title="Impossible de charger l'enregistrement"
					onRetry={interviewDetailResult.refetch}
				/>
			</React.Fragment>
		);
	}

	if (interviewDetailResult.type === 'not-found') {
		// Same guard as the processing screen: the local queue has not been read back from disk yet,
		// never "genuinely introuvable" this early.
		if (interviewDetailResult.outboxHydration === 'pending') {
			return (
				<div css={skeletonStackStyle}>
					<Skeleton height="3rem" animation="wave" shape="rectangle" />
					<Skeleton height="8rem" animation="wave" shape="rectangle" />
					<Skeleton height="8rem" animation="wave" shape="rectangle" />
					<Skeleton height="8rem" animation="wave" shape="rectangle" />
				</div>
			);
		}

		if (interviewDetailResult.outboxHydration === 'failed') {
			return (
				<React.Fragment>
					<PageHeader eyebrow="ENTRETIENS" title="Enregistrement" />
					<ErrorState
						title="Échec de chargement"
						description="La file de transfert locale n'a pas pu être lue sur cet appareil."
						onRetry={handleReloadApplication}
						retryLabel="Recharger l'application"
					/>
				</React.Fragment>
			);
		}

		return (
			<React.Fragment>
				<PageHeader eyebrow="ENTRETIENS" title="Enregistrement" />
				<EmptyState
					title="Enregistrement introuvable"
					description="Cet enregistrement n'existe ni sur cet appareil, ni dans ce dossier côté serveur."
				/>
			</React.Fragment>
		);
	}

	// We redeclare it here just for type safety
	const { interviewDetails } = interviewDetailResult;

	// An interview the server does not know yet has no page of its own, only its transfer.
	if (interviewDetails.origin === 'local' && interviewDetails.localInterviewId !== null) {
		return (
			<Navigate
				to={`${buildSubjectPath(subject)}/local-interviews/${interviewDetails.localInterviewId}/upload`}
				replace
			/>
		);
	}

	// The notice stands in for the transcript, so it shows only while there is none to review.
	const pendingNoticeProps =
		transcriptReviewSectionProps === null &&
		interviewDetails.interviewId !== null &&
		interviewDetails.processingState !== null
			? {
					interviewId: interviewDetails.interviewId,
					processingStateValue: interviewDetails.processingState.value,
				}
			: null;

	// A capture still on the device has no server object to rename.
	const headerTitle =
		interviewDetails.origin === 'server' && interviewDetails.interviewId !== null ? (
			<InterviewTitle
				subject={subject}
				interviewId={interviewDetails.interviewId}
				title={interviewDetails.title}
				canWriteDossier={isWritable}
			/>
		) : (
			interviewDetails.title
		);

	return (
		<div css={rootStyle}>
			<PageHeader
				eyebrow="ENTRETIENS"
				title={headerTitle}
				description={interviewDetails.dateSubtitle ?? undefined}
				actions={
					<React.Fragment>
						<ConditionalLoader data={canSeeConsent}>
							<Link
								css={consentLinkStyle}
								to={`${buildSubjectPath(subject)}/interviews/${interviewId}/consent`}
							>
								Voir le consentement
							</Link>
						</ConditionalLoader>
						<ConditionalLoader data={hasLoadedTranscript}>
							<WithPermission permission={PermissionEnum.DOCUMENT_GENERATION_LAUNCH}>
								<Button
									type="button"
									label="Générer des documents"
									onClick={handleGenerateDocuments}
								/>
							</WithPermission>
						</ConditionalLoader>
						<ConditionalLoader data={interviewDetails.interviewId}>
							{(interviewId) => (
								<WithPermission permission={PermissionEnum.INTERVIEW_DELETE}>
									<ConditionalLoader data={isWritable}>
										<DeleteInterviewAction
											kind={interviewDetails.kind}
											interviewId={interviewId}
											isDisabled={isDeletionInProgress}
										/>
									</ConditionalLoader>
								</WithPermission>
							)}
						</ConditionalLoader>
					</React.Fragment>
				}
			/>

			<ConditionalLoader data={interviewDetails.kind === InterviewKind.CONVERSATION}>
				<InterviewParticipantBadges participants={interviewDetails.participants} />
			</ConditionalLoader>

			<div css={scrollAreaStyle}>
				<ConditionalLoader data={pendingNoticeProps}>
					{(props) => (
						<TranscriptPendingNotice
							subject={subject}
							interviewId={props.interviewId}
							preparationId={interviewDetails.preparationId}
							transcriptionIssue={interviewDetails.transcriptionIssue}
							processingStateValue={props.processingStateValue}
							isWritable={isWritable}
						/>
					)}
				</ConditionalLoader>

				<ConditionalLoader data={transcriptReviewSectionProps}>
					{(props) => (
						<TranscriptReviewSection
							subject={subject}
							interviewId={props.interviewId}
							transcript={props.transcript}
							recordingStatus={props.recordingStatus}
							recordingId={props.recordingId}
							showsAudio={!interviewDetails.isTextEntry}
							participants={props.participants}
							isWritable={isWritable}
						/>
					)}
				</ConditionalLoader>
			</div>
		</div>
	);
};

export default InterviewDetailsContent;
